/**
 * iter98 — Public Client Progress Tracker `/track/:token`.
 *
 * Read-only, auth-free view for couples/clients to see:
 *   • Contracted deliverables (from the shoot crew group meta)
 *   • Payment summary: total billed / paid / pending + progress bar
 *   • Milestone list: what's received, what's still due
 *
 * Data source: `GET /api/public/track/{token}` — same token used by the
 * existing contract signing flow, so no new URL scheme is needed. The
 * financial numbers come straight from `payment_milestones` (admin's own
 * ground truth) so the tracker is always in sync with the admin panel.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { inr } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Camera, IndianRupee, CheckCircle2, Clock3, FileText, MapPin, Calendar as CalendarIcon, ArrowRight, Pencil, Loader2, AlertCircle, Crown, MessageCircle, Palette, ExternalLink, Users, Download, Copy, Check, QrCode, Building2, Wallet } from "lucide-react";
import PaymentProofUploader from "@/components/PaymentProofUploader";
import PayNowModal from "@/components/PayNowModal";
import StudioLogo from "@/components/StudioLogo";
import { useBranding } from "@/lib/branding";
import KBRJFooter from "@/components/KBRJFooter";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function ClientTracker() {
  const { token } = useParams();
  const branding = useBranding();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // iter120 — Financial Blindness: when an internal manager / coordinator
  // opens a client's tracker (via the admin "View" link), hide the entire
  // payment section. Anonymous public visitors (client-facing use) still
  // see their own payments — nothing changes for them.
  const hideFinancials = !!user && user.role !== "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  // iter182 — Pay Now modal state + shared payment settings.
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [payNowMilestone, setPayNowMilestone] = useState(null);
  const [preselectProofMilestoneId, setPreselectProofMilestoneId] = useState(null);

  // iter123 — Load handler exposed as a stable callback so the client
  // moodboard submit can request a fresh payload after saving.
  const load = useCallback(async () => {
    try {
      setLoading(true); setErr("");
      const { data } = await axios.get(`${API}/public/track/${token}`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Tracker unavailable");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setErr("");
        const { data } = await axios.get(`${API}/public/track/${token}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message || "Tracker unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // iter182 — Fetch global studio payment details once so both the
  // Make-a-Payment panel and the Pay Now modal reuse the same source.
  useEffect(() => {
    if (hideFinancials) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/payment-details`);
        if (alive) setPaymentDetails(data);
      } catch {
        if (alive) setPaymentDetails(null);
      }
    })();
    return () => { alive = false; };
  }, [hideFinancials]);

  if (loading) {
    // iter147 — Skeleton-based tracker loading state matching the
    // real card layout so the page stabilises immediately.
    return (
      <div className="min-h-screen bg-[#f9f8f6]" data-testid="tracker-loading" aria-busy="true">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="kb-skeleton h-3 w-24 mb-3" />
            <div className="kb-skeleton h-6 w-72 max-w-full mb-4" />
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="kb-skeleton h-14 rounded-lg" />
              <div className="kb-skeleton h-14 rounded-lg" />
              <div className="kb-skeleton h-14 rounded-lg" />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm space-y-3">
            <div className="kb-skeleton h-4 w-40" />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="kb-skeleton h-10 w-full rounded-lg" />
            ))}
          </div>
          <span className="sr-only">Loading your project…</span>
        </div>
      </div>
    );
  }
  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#f9f8f6]" data-testid="tracker-error">
      <div className="max-w-md w-full text-center bg-white border border-stone-200 rounded-2xl p-8 shadow-sm kb-fade-in">
        <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mb-4">
          <AlertCircle className="w-5 h-5 text-rose-600" strokeWidth={1.8} aria-hidden="true" />
        </div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-stone-500 mb-2 font-semibold">Kahani Studio</div>
        <div className="text-rose-900 font-heading text-2xl mb-2 font-semibold">Tracker unavailable</div>
        <div className="text-stone-500 text-sm leading-relaxed">{err}</div>
      </div>
    </div>
  );
  if (!data) return null;

  const { client_name, place, event_dates = [], payments = {}, milestones = [] } = data;
  // iter122 — Extract new payload sections.
  const nextPaymentDue = data.next_payment_due || null;
  const shootLead = data.shoot_lead || null;
  const moodboard = data.moodboard || { urls: "", notes: "" };
  const moodboardUrlList = String(moodboard.urls || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  // iter113 — Header displays the full couple name (e.g., "Rakesh & Preeti")
  // when the backend supplies it; falls back to `client_name` otherwise so
  // legacy contracts still render something.
  const displayName = String(data.couple_name || "").trim() || client_name;
  // iter100 — Normalise to Array before render. If the backend ever returns
  // a string (legacy), we split on newlines; if it returns null/undefined,
  // we render the placeholder branch.
  const rawDel = data.contracted_deliverables;
  const contracted_deliverables = Array.isArray(rawDel)
    ? rawDel
    : typeof rawDel === "string"
      ? rawDel.split("\n").map((s) => s.trim().replace(/^[•\-]\s*/, "")).filter(Boolean)
      : [];
  // iter105 — Prefer v2 (status objects) when the backend emits them; fall
  // back to the flat list when talking to a pre-iter105 server.
  const deliverablesWithStatus = Array.isArray(data.contracted_deliverables_v2) && data.contracted_deliverables_v2.length > 0
    ? data.contracted_deliverables_v2
    : contracted_deliverables.map((label) => ({ label, status: "pending" }));
  const { total_billed = 0, total_paid = 0, pending_balance = 0, percent_paid = 0 } = payments;

  // iter182 — Resolve the actual milestone object behind the
  // `next_payment_due` summary so PayNow / proof preselect have the real id.
  const nextPaymentMilestone = nextPaymentDue
    ? milestones.find(
        (m) =>
          m.status !== "received" &&
          (m.milestone_type === nextPaymentDue.milestone_type ||
            m.label === nextPaymentDue.label),
      )
    : null;

  const openPayNow = (m) => setPayNowMilestone(m);
  const closePayNow = () => setPayNowMilestone(null);
  const onPaidConfirmed = (m) => {
    // Feed the milestone id to PaymentProofUploader so it preselects
    // the correct radio + scrolls into view (+ visual highlight).
    setPreselectProofMilestoneId(m?.id || "other");
    // Reset the trigger after a moment so the same button can be
    // clicked again for a repeat proof upload if needed.
    setTimeout(() => setPreselectProofMilestoneId(null), 3000);
  };

  return (
    <div className="min-h-screen bg-[#FDF7EF]" data-testid="client-tracker-page">
      {/* iter180 — Luxury editorial hero. Ivory background + champagne
           accents matching the Kahani proposal + luxury invoice. Logo
           dark variant chosen so the KBRJ mark reads on the cream bg. */}
      <header className="border-b border-[#E7D5B4]/70 px-6 pt-10 pb-8 bg-gradient-to-b from-[#FDF7EF] to-[#F7ECD9]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-3 flex justify-center" data-testid="tracker-studio-logo">
            <StudioLogo size={48} variant="dark" className="max-h-[48px]" fallbackTextClassName="font-serif text-[#3B2F24] tracking-[0.2em]" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#9C7550] font-semibold mb-3">
            {branding.studio_name} · Project Tracker
          </div>
          <div className="mx-auto w-10 border-t border-[#E7D5B4] mb-3" />
          <h1 className="font-heading text-3xl sm:text-4xl text-[#3B2F24] tracking-tight" data-testid="tracker-client-name">
            {displayName}
          </h1>
          <div className="text-[#6E5B49] text-sm mt-3 flex flex-wrap items-center justify-center gap-3">
            {place && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" strokeWidth={2} /> {place}</span>}
            {event_dates.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5" strokeWidth={2} />
                {event_dates.map(fmtDate).join(" · ")}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* iter178 — YOUR EVENTS: per-date event list from the linked
             booking. Sorted chronologically. Hidden when empty so old
             contracts without a booking link don't show a blank panel. */}
        {Array.isArray(data.events) && data.events.length > 0 && (
          <section className="bg-white rounded-lg border border-[#E7D5B4]/70 shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-4 md:p-6" data-testid="tracker-your-events">
            <div className="flex items-center gap-2 mb-4">
              <CalendarIcon className="w-3.5 h-3.5 text-[#9C7550]" strokeWidth={2} />
              <h2 className="editorial-eyebrow text-[#9C7550]">Your Events</h2>
              <span className="ml-auto text-[10px] uppercase tracking-[0.3em] text-[#B18860] font-semibold">
                {data.events.length} {data.events.length === 1 ? "day" : "days"}
              </span>
            </div>
            <ul className="divide-y divide-[#E7D5B4]/60" data-testid="tracker-events-list">
              {data.events.map((ev, i) => (
                <li key={`${ev.date}-${i}`} className="py-4 flex items-center gap-5" data-testid={`tracker-event-${i}`}>
                  <div className="text-center w-16 shrink-0">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[#9C7550] font-semibold">
                      {new Date(ev.date + "T00:00:00").toLocaleString("en-IN", { month: "short" })}
                    </div>
                    <div className="font-heading text-3xl leading-none text-[#3B2F24] mt-0.5">
                      {new Date(ev.date + "T00:00:00").getDate()}
                    </div>
                    <div className="text-[10px] text-[#B18860] tracking-widest mt-0.5">
                      {new Date(ev.date + "T00:00:00").getFullYear()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 border-l border-[#E7D5B4]/60 pl-5">
                    <div className="font-heading text-lg text-[#3B2F24]" data-testid={`tracker-event-name-${i}`}>
                      {ev.event || "Event"}
                    </div>
                    {ev.location && (
                      <div className="text-xs text-[#6E5B49] flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" strokeWidth={2} /> {ev.location}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
        {/* iter122 — Next Payment Due highlight banner. Only surfaces when
             the client has an unpaid installment. Hidden for internal
             manager/coordinator (Financial Blindness, iter120). */}
        {!hideFinancials && nextPaymentDue && (
          <section
            className={`rounded-lg border shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-5 flex items-start gap-4 ${
              nextPaymentDue.status === "overdue"
                ? "bg-[#FBEBE9] border-[#D48A78]"
                : "bg-[#FDF7EF] border-[#E7D5B4]"
            }`}
            data-testid="tracker-next-payment-banner"
          >
            <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
              nextPaymentDue.status === "overdue" ? "bg-white text-[#B04A32]" : "bg-white text-[#9C7550]"
            }`}>
              <AlertCircle className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="editorial-eyebrow text-[#9C7550] mb-1">
                Next Payment Due
              </div>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-heading text-3xl text-[#3B2F24]" data-testid="tracker-next-payment-amount">
                  {inr(nextPaymentDue.amount)}
                </span>
                <span className="text-sm text-[#6E5B49]">— {nextPaymentDue.label}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-[#6E5B49]">Due {fmtDate(nextPaymentDue.due_date)}</span>
                {nextPaymentDue.status === "overdue" ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-[#B04A32] text-white" data-testid="tracker-next-payment-status">
                    Overdue
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-[#9C7550] text-white" data-testid="tracker-next-payment-status">
                    Pending
                  </span>
                )}
              </div>
              {/* iter182 — PAY NOW CTA. Opens the UPI intent / QR modal
                   for the resolved milestone (or the summary itself as a
                   fallback if legacy data has no matching milestone id). */}
              {paymentDetails?.upi_id && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() =>
                      openPayNow(
                        nextPaymentMilestone || {
                          id: "next",
                          label: nextPaymentDue.label,
                          milestone_type: nextPaymentDue.milestone_type,
                          amount: nextPaymentDue.amount,
                          paid_amount: 0,
                        },
                      )
                    }
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3B2F24] text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#231b13] transition-colors shadow-sm"
                    data-testid="tracker-pay-now-next-btn"
                  >
                    <Wallet className="w-3.5 h-3.5" strokeWidth={2} />
                    Pay Now · {inr(nextPaymentDue.amount)}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* iter122 — Shoot Lead Contact Card. Always renders (not financial). */}
        <section className="bg-white rounded-lg border border-[#E7D5B4]/70 shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-4 md:p-6" data-testid="tracker-shoot-lead-card">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-3.5 h-3.5 text-[#9C7550]" strokeWidth={2} />
            <h2 className="editorial-eyebrow text-[#9C7550]">Your Studio Shoot Lead</h2>
          </div>
          {shootLead ? (
            <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-full bg-[#F5EBDC] border border-[#E7D5B4] flex items-center justify-center text-[#9C7550] font-heading text-xl shrink-0">
                  {(shootLead.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg text-[#3B2F24] break-words" data-testid="tracker-shoot-lead-name">
                    {shootLead.name}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#9C7550] font-semibold mt-0.5" data-testid="tracker-shoot-lead-role">
                    {shootLead.role || "Shoot Lead"}
                  </div>
                </div>
              </div>
              {shootLead.wa_me_url ? (
                <a
                  href={shootLead.wa_me_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#3F6B4A] text-white text-sm font-semibold hover:bg-[#365a3f] transition-colors w-full md:w-auto md:ml-auto"
                  data-testid="tracker-shoot-lead-wa"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={2} />
                  WhatsApp Shoot Lead
                </a>
              ) : (
                <span className="text-[11px] text-[#B18860] italic">Contact number not yet shared</span>
              )}
            </div>
          ) : (
            <div className="text-sm text-[#6E5B49] italic" data-testid="tracker-shoot-lead-unassigned">
              Team Lead: To Be Assigned
            </div>
          )}
        </section>

        {/* Payment summary — hidden for internal managers/coordinators (iter120) */}
        {!hideFinancials && (
        <section className="bg-white rounded-lg border border-[#E7D5B4]/70 shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-4 md:p-6" data-testid="tracker-payment-summary">
          <div className="flex items-center gap-2 mb-5">
            <IndianRupee className="w-3.5 h-3.5 text-[#9C7550]" strokeWidth={2} />
            <h2 className="editorial-eyebrow text-[#9C7550]">Payment Progress</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <SummaryStat label="Total billed" amount={total_billed} testid="tracker-total-billed" />
            <SummaryStat label="Paid" amount={total_paid} tone="emerald" testid="tracker-total-paid" />
            <SummaryStat label="Pending" amount={pending_balance} tone={pending_balance > 0 ? "rose" : "stone"} testid="tracker-pending-balance" />
          </div>

          {/* Progress bar */}
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-[#9C7550]">{percent_paid}% paid</span>
            <span className="text-[11px] text-[#B18860] tabular-nums">{inr(total_paid)} / {inr(total_billed)}</span>
          </div>
          <div className="h-1.5 w-full bg-[#F5EBDC] rounded-full overflow-hidden" data-testid="tracker-progress-bar">
            <div
              className="h-full bg-[#9C7550] transition-all"
              style={{ width: `${Math.min(100, Math.max(0, percent_paid))}%` }}
            />
          </div>
        </section>
        )}

        {/* iter178 — Download Invoice + Make a Payment.
             Both sections are hidden from internal managers/coordinators
             (Financial Blindness, iter120). They read from public
             endpoints — the couple's tracker token is the capability. */}
        {!hideFinancials && (
          <PaymentActionsPanel
            token={token}
            milestones={milestones}
            details={paymentDetails}
            preselectMilestoneId={preselectProofMilestoneId}
          />
        )}

        {/* iter146 — Split-Payment section (merged from the deprecated
             /track/split/:token page). Renders one card per configured
             party with assigned/paid/pending + progress + due dates.
             Hidden for internal managers/coordinators (Financial
             Blindness, iter120) and when no split is configured. */}
        {!hideFinancials && data.payment_split && data.payment_split.enabled && (
          <section id="payment-split" className="bg-white rounded-lg border border-[#E7D5B4]/70 shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-4 md:p-6" data-testid="tracker-payment-split">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-3.5 h-3.5 text-[#9C7550]" strokeWidth={2} />
              <h2 className="editorial-eyebrow text-[#9C7550]">Split Payment</h2>
              <span className="ml-2 text-[10px] uppercase tracking-widest font-semibold text-[#B18860]">
                {data.payment_split.party_count} {data.payment_split.party_count === 1 ? "party" : "parties"}
              </span>
            </div>
            <ul className="space-y-4">
              {data.payment_split.parties.map((party, idx) => {
                const st = String(party.status || "pending").toLowerCase();
                const badge = st === "paid"
                  ? { cls: "bg-[#E7EFE7] text-[#3F6B4A] border-[#c9dcc9]", label: "Paid" }
                  : st === "partial"
                    ? { cls: "bg-[#EDE5D6] text-[#7A5A2E] border-[#dbc9a3]", label: `Partial · ${party.percent_paid}%` }
                    : st === "overdue"
                      ? { cls: "bg-[#B04A32] text-white border-[#963e2a]", label: "Overdue" }
                      : { cls: "bg-[#FDF7EF] text-[#9C7550] border-[#E7D5B4]", label: "Pending" };
                return (
                  <li key={party.id || idx} className="border border-[#E7D5B4]/70 rounded-md p-4 bg-[#FDF7EF]/60" data-testid={`tracker-split-party-${idx}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <div className="font-heading text-lg text-[#3B2F24] truncate" data-testid={`tracker-split-party-name-${idx}`}>
                          {party.name}
                        </div>
                        {party.relationship && (
                          <div className="text-[11px] text-[#9C7550] uppercase tracking-widest">{party.relationship}</div>
                        )}
                      </div>
                      <span
                        className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${badge.cls}`}
                        data-testid={`tracker-split-party-status-${idx}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                      <SummaryStat label="Assigned" amount={party.assigned} testid={`tracker-split-party-assigned-${idx}`} />
                      <SummaryStat label="Paid" amount={party.paid} tone="emerald" testid={`tracker-split-party-paid-${idx}`} />
                      <SummaryStat label="Pending" amount={party.pending} tone={party.pending > 0 ? "rose" : "stone"} testid={`tracker-split-party-pending-${idx}`} />
                    </div>
                    <div className="mb-3">
                      <div className="mb-1 flex items-baseline justify-between">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-[#9C7550]">{party.percent_paid}% paid</span>
                        <span className="text-[10px] text-[#B18860] tabular-nums">{inr(party.paid)} / {inr(party.assigned)}</span>
                      </div>
                      <div className="h-1.5 w-full bg-[#F5EBDC] rounded-full overflow-hidden" data-testid={`tracker-split-party-progress-${idx}`}>
                        <div
                          className="h-full bg-[#9C7550] transition-all"
                          style={{ width: `${Math.min(100, Math.max(0, Number(party.percent_paid) || 0))}%` }}
                        />
                      </div>
                    </div>
                    {Array.isArray(party.schedule) && party.schedule.length > 0 && (
                      <ul className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {party.schedule.map((row, j) => (
                          <li
                            key={j}
                            className="bg-white border border-[#E7D5B4]/70 rounded-md px-3 py-2"
                            data-testid={`tracker-split-party-${idx}-schedule-${j}`}
                          >
                            <div className="text-[9px] uppercase tracking-widest text-[#9C7550] font-semibold">
                              {row.label}
                            </div>
                            <div className="font-heading text-lg text-[#3B2F24] mt-1">
                              {inr(row.amount)}
                              <span className="ml-1 text-[10px] text-[#B18860]">· {row.percent}%</span>
                            </div>
                            {row.due_date_pretty && (
                              <div className="text-[10px] text-[#6E5B49] mt-0.5">
                                Due {row.due_date_pretty}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {Array.isArray(party.payment_history) && party.payment_history.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#E7D5B4]/60" data-testid={`tracker-split-party-${idx}-payments`}>
                        <div className="text-[9px] uppercase tracking-widest text-[#9C7550] font-semibold mb-1.5">
                          Payment History ({party.payment_history.length})
                        </div>
                        <ul className="space-y-1 text-[11px]">
                          {party.payment_history.map((rec, j) => (
                            <li key={rec.id || j} className={`flex items-baseline justify-between px-2 py-1 rounded ${rec.is_adjustment ? "bg-[#FDF7EF] border border-[#E7D5B4]" : "bg-white border border-[#E7D5B4]/50"}`}>
                              <div className="min-w-0 flex-1">
                                <span className={`font-mono font-semibold ${rec.amount < 0 ? "text-[#B04A32]" : "text-[#3F6B4A]"}`}>
                                  {rec.amount < 0 ? "-" : "+"}{inr(Math.abs(rec.amount))}
                                </span>
                                <span className="ml-2 text-[#9C7550] uppercase tracking-widest text-[9px]">
                                  {rec.is_adjustment ? "Adjustment" : (rec.mode || "").toUpperCase()}
                                </span>
                              </div>
                              <div className="text-[#B18860] shrink-0 tabular-nums">
                                {rec.payment_date ? String(rec.payment_date).slice(0, 10) : ""}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* Milestones — also gated behind Financial Blindness (iter120) */}
        {!hideFinancials && milestones.length > 0 && (
          <section className="bg-white rounded-lg border border-[#E7D5B4]/70 shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-4 md:p-6" data-testid="tracker-milestones">
            <div className="flex items-center gap-2 mb-4">
              <Clock3 className="w-3.5 h-3.5 text-[#9C7550]" strokeWidth={2} />
              <h2 className="editorial-eyebrow text-[#9C7550]">Payment Milestones</h2>
            </div>
            <ul className="divide-y divide-[#E7D5B4]/60">
              {milestones.map((m) => {
                const todayIso = new Date().toISOString().slice(0, 10);
                const paidAmt = Number(m.paid_amount || 0);
                const isPaid = m.status === "received";
                const isPartial = m.status === "partial" || (paidAmt > 0 && paidAmt < Number(m.amount || 0));
                const isOverdue = !isPaid && !isPartial && m.due_date && m.due_date < todayIso;
                const badgeClass = isPaid
                  ? "bg-[#E7EFE7] text-[#3F6B4A] border-[#c9dcc9]"
                  : isPartial
                  ? "bg-[#EDE5D6] text-[#7A5A2E] border-[#dbc9a3]"
                  : isOverdue
                  ? "bg-[#B04A32] text-white border-[#963e2a]"
                  : "bg-[#FDF7EF] text-[#9C7550] border-[#E7D5B4]";
                const badgeLabel = isPaid
                  ? "Paid"
                  : isPartial
                  ? `Partial · ${inr(paidAmt)} of ${inr(m.amount)}`
                  : isOverdue
                  ? "Overdue"
                  : "Pending";
                return (
                <li key={m.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`tracker-milestone-${m.milestone_type}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {isPaid ? (
                      <CheckCircle2 className="w-5 h-5 text-[#3F6B4A] shrink-0" strokeWidth={1.8} />
                    ) : isPartial ? (
                      <div className="w-5 h-5 rounded-full border border-[#9C7550] bg-[#F5EBDC] shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border border-[#E7D5B4] shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="font-heading text-[#3B2F24] truncate">{m.label || m.milestone_type}</div>
                      <div className="text-[11px] text-[#6E5B49]">
                        {isPaid
                          ? `Received ${fmtDate(m.paid_date || m.due_date)}`
                          : `Due ${fmtDate(m.due_date)}`}
                        {m.note ? ` · ${m.note}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 sm:justify-end flex-wrap">
                    <span
                      className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${badgeClass}`}
                      data-testid={`tracker-milestone-status-${m.milestone_type}`}
                    >
                      {badgeLabel}
                    </span>
                    <div className={`font-heading text-lg tabular-nums ${isPaid ? "text-[#3F6B4A]" : isPartial ? "text-[#7A5A2E]" : "text-[#3B2F24]"}`}>
                      {inr(m.amount)}
                      {m.percent ? <span className="text-[10px] text-[#B18860] ml-1">· {Math.round(m.percent * 100)}%</span> : null}
                    </div>
                    {/* iter182 — Per-milestone Pay Now button. Only for
                         unpaid or partial; requires studio UPI configured. */}
                    {!isPaid && paymentDetails?.upi_id && (
                      <button
                        type="button"
                        onClick={() => openPayNow(m)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#3B2F24] text-white text-[10px] font-semibold uppercase tracking-widest hover:bg-[#231b13] transition-colors"
                        data-testid={`tracker-pay-now-btn-${m.milestone_type}`}
                      >
                        <Wallet className="w-3 h-3" strokeWidth={2} />
                        Pay Now
                      </button>
                    )}
                  </div>
                </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* iter122 — Moodboard & Inspiration. Always rendered — not financial,
             so Manager sees it. Empty state guides the studio to add references. */}
        <section className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6" data-testid="tracker-moodboard">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-fuchsia-700" strokeWidth={2.2} />
            <h2 className="font-heading text-lg font-semibold">Moodboard &amp; Inspiration</h2>
          </div>
          {moodboardUrlList.length === 0 && !moodboard.notes ? (
            <div className="text-sm text-stone-500 italic" data-testid="tracker-moodboard-empty">
              Rakesh will curate a Pinterest board + reference photos for your shoot here soon.
              <span className="block mt-1 text-stone-600">Have your own vision? Share it below and it'll reach the team instantly.</span>
            </div>
          ) : (
            <div className="space-y-3">
              {moodboardUrlList.length > 0 && (
                <ul className="grid sm:grid-cols-2 gap-2" data-testid="tracker-moodboard-urls">
                  {moodboardUrlList.map((url, i) => {
                    // iter123 — Client-submitted links are prefixed by an
                    // "— submitted by …" header line. Render those as tiny
                    // section headers so the studio and clients see who
                    // added what without needing a separate collection.
                    if (url.startsWith("— submitted by")) {
                      return (
                        <li key={`hdr-${i}`} className="sm:col-span-2 pt-2">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-700 border-t border-fuchsia-200 pt-2">
                            {url.replace(/^—\s*/, "").replace(/\s*—$/, "")}
                          </div>
                        </li>
                      );
                    }
                    return (
                      <li key={`${url}-${i}`}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-start gap-1.5 text-sm text-fuchsia-800 hover:text-fuchsia-950 bg-white border border-fuchsia-200 hover:border-fuchsia-400 rounded-lg px-3 py-2 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="truncate break-all">{url}</span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
              {moodboard.notes && (
                <div className="text-sm text-stone-700 whitespace-pre-wrap bg-fuchsia-50/50 border border-fuchsia-200 rounded-lg px-3 py-2" data-testid="tracker-moodboard-notes">
                  {moodboard.notes}
                </div>
              )}
            </div>
          )}

          {/* iter123 — Client-facing submission form. Anonymous clients
               visiting /track/{token} can paste their own Pinterest /
               Instagram / Drive links + a short note. Internal users
               (admin/manager/coord) still use the Shoot Crew editor. */}
          {!user && (
            <ClientMoodboardSubmit token={token} onSaved={load} />
          )}
        </section>

        {/* iter100 — ALWAYS render the deliverables section. Historically
             this branch was `contracted_deliverables.length > 0 && …`
             which silently hid the section for clients where the studio
             hadn't finished setting up the group meta yet (e.g. Aishwarya's
             Baby Shower). We now render a friendly placeholder line so
             clients see the section exists and is coming. */}
        <section className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6" data-testid="tracker-deliverables">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-stone-700" strokeWidth={2.2} />
              <h2 className="font-heading text-lg font-semibold">Contracted deliverables</h2>
            </div>
            {/* iter102 — Admin-only quick edit shortcut. Jumps straight to
                the Shoot Crew card's Deliverables & Terms editor for this
                (client, place) so Rakesh can fill in missing deliverables
                without navigating manually. Uses query params so Team +
                ShootCrew can auto-select the tab, scroll to the card, and
                auto-open + focus the deliverables textarea. */}
            {isAdmin && (
              <Link
                to={`/team?tab=shoot-crew&editClient=${encodeURIComponent(client_name || "")}&editPlace=${encodeURIComponent(place || "")}&openDeliverables=1`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:text-rose-900 border border-rose-200 hover:border-rose-300 bg-rose-50 hover:bg-rose-100 rounded-full px-2.5 py-1 transition-colors"
                title="Edit contracted deliverables in Shoot Crew Management"
                data-testid="tracker-edit-deliverables-btn"
              >
                <Pencil className="w-3 h-3" strokeWidth={2.4} />
                Edit
              </Link>
            )}
          </div>
          {Array.isArray(deliverablesWithStatus) && deliverablesWithStatus.length > 0 ? (
            <ul className="space-y-2 text-sm text-stone-700">
              {deliverablesWithStatus.map((d, i) => {
                const status = String(d.status || "pending").toLowerCase();
                const badge = status === "done"
                  ? { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} />, label: "Completed" }
                  : status === "in_progress"
                    ? { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.4} />, label: "In Progress" }
                    : { cls: "bg-stone-100 text-stone-600 border-stone-200", icon: <Clock3 className="w-3 h-3" strokeWidth={2.4} />, label: "Pending" };
                return (
                  <li key={i} className="flex items-start justify-between gap-3" data-testid={`tracker-deliverable-${i}`}>
                    <div className="flex items-start gap-2 min-w-0">
                      <ArrowRight className="w-3.5 h-3.5 text-rose-700 mt-0.5 shrink-0" strokeWidth={2.2} />
                      <span className={status === "done" ? "line-through text-stone-500" : ""}>{String(d.label || "")}</span>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${badge.cls}`}
                      data-testid={`tracker-deliverable-status-${i}`}
                    >
                      {badge.icon}
                      {badge.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-sm text-stone-500 italic bg-stone-50 border border-dashed border-stone-200 rounded-lg px-4 py-3" data-testid="tracker-deliverables-placeholder">
              Deliverables are being finalised by the studio — they'll appear here shortly.
            </div>
          )}
        </section>

        <div className="text-center text-[11px] text-stone-400 pt-2">
          <FileText className="w-3 h-3 inline -mt-0.5 mr-1" strokeWidth={2} />
          Live tracker · updates automatically as your studio marks milestones paid or received.
        </div>
      </main>

      <KBRJFooter />

      {/* iter182 — Pay Now modal (UPI intent / QR). Renders per-milestone. */}
      <PayNowModal
        open={!!payNowMilestone}
        onClose={closePayNow}
        milestone={payNowMilestone}
        details={paymentDetails}
        coupleName={displayName}
        onPaidConfirmed={onPaidConfirmed}
      />
    </div>
  );
}

function SummaryStat({ label, amount, tone = "stone", testid }) {
  const tones = {
    stone: "text-[#3B2F24]",
    emerald: "text-[#3F6B4A]",
    rose: "text-[#B04A32]",
  };
  return (
    <div className="bg-[#FDF7EF] border border-[#E7D5B4]/60 rounded-md px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#9C7550] font-semibold mb-1">{label}</div>
      <div className={`font-heading text-2xl tabular-nums ${tones[tone] || tones.stone}`}>{inr(amount)}</div>
    </div>
  );
}

// ==================================================================
// iter123 — Client-facing Moodboard Submission form
// ------------------------------------------------------------------
// Anonymous couple pastes their Pinterest / Instagram / Drive links +
// a short note. Backend appends (never overwrites) so studio content
// stays intact. Uses a toast for feedback and refreshes the tracker
// so the newly-added links appear immediately.
// ==================================================================
function ClientMoodboardSubmit({ token, onSaved }) {
  const [urls, setUrls] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!urls.trim() && !notes.trim()) {
      toast.error("Add at least one link or a short note.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/public/track/${token}/moodboard`, {
        urls: urls.trim(),
        notes: notes.trim(),
      });
      const added = data?.added_url_count || 0;
      toast.success(
        added > 0
          ? `Sent ${added} link${added === 1 ? "" : "s"} to your Kahani team ✨`
          : `Your note reached Kahani ✨`,
      );
      setUrls("");
      setNotes("");
      setSaved(true);
      if (onSaved) await onSaved();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mt-5 pt-5 border-t border-fuchsia-100 space-y-3"
      data-testid="tracker-moodboard-submit-form"
    >
      <div className="text-[11px] uppercase tracking-widest font-bold text-fuchsia-800">
        ✨ Share Your Vision
      </div>
      <label className="block">
        <span className="block text-[11px] font-semibold text-stone-600 mb-1">
          Paste your Pinterest / Instagram / Drive reference links here
        </span>
        <textarea
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          rows={3}
          placeholder={"https://pin.it/…\nhttps://www.instagram.com/p/…\nhttps://drive.google.com/…"}
          className="input text-xs font-mono py-2 !bg-white"
          data-testid="tracker-moodboard-input-urls"
        />
        <span className="block text-[10px] text-stone-400 mt-1">
          Each link on a new line · only http/https links are saved
        </span>
      </label>
      <label className="block">
        <span className="block text-[11px] font-semibold text-stone-600 mb-1">
          Notes (colours, feelings, must-have shots — anything!)
        </span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="e.g., Warm sunset palette, candid moments over posed shots…"
          className="input text-xs py-2 !bg-white"
          data-testid="tracker-moodboard-input-notes"
        />
      </label>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] ${saved ? "text-emerald-700 font-semibold" : "text-stone-500"}`}>
          {saved ? "✓ Sent to Kahani" : "Rakesh & your Shoot Lead will see this instantly"}
        </span>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-fuchsia-700 text-white text-sm font-semibold hover:bg-fuchsia-800 disabled:opacity-60 shadow-sm"
          data-testid="tracker-moodboard-submit-btn"
        >
          {saving ? "Saving…" : "Save & Share"}
        </button>
      </div>
    </form>
  );
}



// iter178 — Client-facing "Make a Payment" + "Download Invoice" panel.
// Fetches the studio's public payment details singleton and renders
// bank + UPI + optional QR alongside a Download Invoice button.
// iter182 — When the parent (ClientTracker) already fetched the
// payment settings + wants to preselect a milestone for proof upload,
// it may pass `details` + `preselectMilestoneId` as props, avoiding a
// duplicate network call.
function PaymentActionsPanel({ token, milestones = [], details: detailsProp = null, preselectMilestoneId = null }) {
  const [detailsFetched, setDetailsFetched] = useState(null);
  const [loading, setLoading] = useState(detailsProp === null);
  useEffect(() => {
    if (detailsProp !== null) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/payment-details`);
        if (alive) setDetailsFetched(data);
      } catch {
        if (alive) setDetailsFetched(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [detailsProp]);
  const details = detailsProp ?? detailsFetched;

  const invoiceUrl = `${API}/public/track/${encodeURIComponent(token)}/invoice.pdf`;
  const hasBank = details && (details.account_number || details.ifsc_code || details.bank_name);
  const hasUpi = details && (details.upi_id || details.upi_qr_url);
  const hasAny = details && details.configured;

  return (
    <>
      <section
        className="bg-white rounded-lg border border-[#E7D5B4]/70 shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-4 md:p-6 flex items-center gap-4 flex-wrap"
        data-testid="tracker-invoice-card"
      >
        <div className="w-11 h-11 rounded-full bg-[#F5EBDC] border border-[#E7D5B4] flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-[#9C7550]" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="editorial-eyebrow text-[#9C7550]">Invoice</div>
          <h3 className="font-heading text-xl text-[#3B2F24]">Download your invoice</h3>
          <p className="text-xs text-[#6E5B49] mt-0.5">Always reflects the latest billing &amp; payment info.</p>
        </div>
        <a
          href={invoiceUrl}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#3B2F24] text-white text-sm font-semibold tracking-wide hover:bg-[#231b13] transition-colors"
          data-testid="tracker-download-invoice-btn"
          download
        >
          <Download className="w-4 h-4" strokeWidth={2} /> Download Invoice
        </a>
      </section>

      {!loading && hasAny && (
        <section
          className="bg-white rounded-lg border border-[#E7D5B4]/70 shadow-[0_1px_2px_rgba(60,45,30,0.04)] p-4 md:p-6"
          data-testid="tracker-make-a-payment"
        >
          <div className="flex items-center gap-2 mb-5">
            <IndianRupee className="w-3.5 h-3.5 text-[#9C7550]" strokeWidth={2} />
            <h2 className="editorial-eyebrow text-[#9C7550]">Make a Payment</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {hasBank && (
              <div className="space-y-3" data-testid="tracker-bank-block">
                <div className="editorial-eyebrow text-[#9C7550] flex items-center gap-1.5">
                  <Building2 className="w-3 h-3" strokeWidth={2} /> Bank Transfer
                </div>
                <div className="border-t border-[#E7D5B4]/60 pt-3 space-y-3">
                  {details.account_holder_name && (
                    <PayRow label="Account Holder" value={details.account_holder_name} testid="tracker-bank-holder" />
                  )}
                  {details.bank_name && (
                    <PayRow label="Bank" value={details.bank_name} testid="tracker-bank-name" />
                  )}
                  {details.account_number && (
                    <PayRow label="Account Number" value={details.account_number} copyable testid="tracker-bank-account" />
                  )}
                  {details.ifsc_code && (
                    <PayRow label="IFSC" value={details.ifsc_code} copyable testid="tracker-bank-ifsc" />
                  )}
                </div>
              </div>
            )}

            {hasUpi && (
              <div className="space-y-3" data-testid="tracker-upi-block">
                <div className="editorial-eyebrow text-[#9C7550] flex items-center gap-1.5">
                  <QrCode className="w-3 h-3" strokeWidth={2} /> UPI
                </div>
                <div className="border-t border-[#E7D5B4]/60 pt-3 space-y-3">
                  {details.upi_display_name && (
                    <PayRow label="Name" value={details.upi_display_name} testid="tracker-upi-name" />
                  )}
                  {details.upi_id && (
                    <PayRow label="UPI ID" value={details.upi_id} copyable testid="tracker-upi-id" />
                  )}
                  {details.upi_qr_url && (
                    <div className="pt-1" data-testid="tracker-upi-qr-wrap">
                      <div className="inline-block p-3 border border-[#E7D5B4] rounded-md bg-white">
                        <img
                          src={details.upi_qr_url}
                          alt="UPI QR"
                          className="w-40 h-40 object-contain"
                          data-testid="tracker-upi-qr-img"
                        />
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-[#9C7550] mt-2">Scan with any UPI app</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {details.payment_instructions && (
            <div className="mt-6 rounded-md bg-[#FDF7EF] border border-[#E7D5B4]/60 px-4 py-3 text-xs text-[#6E5B49] leading-relaxed whitespace-pre-line italic" data-testid="tracker-payment-instructions">
              {details.payment_instructions}
            </div>
          )}

          {/* iter181 — Upload Payment Screenshot */}
          <PaymentProofUploader token={token} milestones={milestones} preselectMilestoneId={preselectMilestoneId} />
        </section>
      )}
    </>
  );
}

function PayRow({ label, value, copyable, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#9C7550] font-semibold">
          {label}
        </div>
        <div className="text-sm font-mono text-[#3B2F24] break-all mt-0.5">{value}</div>
      </div>
      {copyable && (
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold text-[#9C7550] hover:text-[#3B2F24] hover:bg-[#F5EBDC] border border-[#E7D5B4] transition-colors"
          data-testid={`${testid}-copy`}
        >
          {copied ? <Check className="w-3 h-3" strokeWidth={2.4} /> : <Copy className="w-3 h-3" strokeWidth={2} />}
          {copied ? "Copied" : "Copy"}
        </button>
      )}
    </div>
  );
}
